import { useCallback, useEffect, useState } from "react";
import { SocialLinks } from "@/context/SettingsContext";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, Search, ShoppingBag, X } from "lucide-react";
import { AccountMenu } from "@/components/layout/AccountMenu";
import { Wordmark } from "@/components/brand/Wordmark";
import { GoodPeopleMark } from "@/components/brand/Lettering";
import { useCart } from "@/context/CartContext";
import { SearchOverlay } from "@/components/layout/SearchOverlay";

const NAV = [["/shop", "Shop"], ["/drops", "Drops"], ["/sus-of-the-month", "Sus of the Month", "SOTM"], ["/lookbook", "Lookbook"], ["/about", "About"]];
const slugOf = (label) => label.toLowerCase().replace(/\s+/g, "-");

const MobileMenu = ({ open, onClose }) => (
  <div className={`overlay-fade fixed inset-0 z-[65] flex flex-col bg-cloud lg:hidden ${open ? "opacity-100" : "pointer-events-none opacity-0"}`} aria-hidden={!open} data-testid="mobile-menu">
    <div className="flex h-[88px] items-center justify-between border-b border-ink/10 px-4">
      <Wordmark size="xl" />
      <button onClick={onClose} aria-label="Close menu" className="p-1" data-testid="mobile-menu-close"><X size={24} strokeWidth={1.5} /></button>
    </div>
    <nav aria-label="Mobile" className="flex-1 px-5 pt-10">
      <ul>
        {NAV.map(([to, label], i) => (
          <li key={to} className="border-b border-ink/10">
            <NavLink to={to} onClick={onClose} className="flex items-baseline justify-between py-3" data-testid={`mobile-nav-${slugOf(label)}-link`}>
              <span className="font-a text-[40px] leading-tight">{label}</span>
              <span className="label text-ink/40">0{i + 1}</span>
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
    <div className="px-5 pb-8">
      <div className="mb-8 flex gap-6"><Link to="/account" onClick={onClose} className="link-u" data-testid="mobile-nav-account-link">My account</Link><Link to="/order-lookup" onClick={onClose} className="link-u" data-testid="mobile-nav-order-lookup-link">Order lookup</Link><Link to="/gift-cards" onClick={onClose} className="link-u" data-testid="mobile-nav-gift-cards-link">Gift cards</Link></div>
      <GoodPeopleMark className="mb-8 ml-2 text-[44px]" />
      <SocialLinks prefix="mobile-social" className="mb-4" linkClass="text-ink/70 hover:text-ink" />
      <p className="label mt-3 text-ink/50">Toronto, Canada · Est. 2024</p>
    </div>
  </div>
);

const IconBtn = ({ label, onClick, testid, children, className = "" }) => (
  <button onClick={onClick} aria-label={label} className={`relative p-2 transition-opacity hover:opacity-60 ${className}`} data-testid={testid}>{children}</button>
);

export const Header = () => {
  const { count, setOpen } = useCart();
  const [menu, setMenu] = useState(false);
  const [search, setSearch] = useState(false);
  const { pathname } = useLocation();
  const closeSearch = useCallback(() => setSearch(false), []);

  useEffect(() => setMenu(false), [pathname]);

  return (
    <>
      <header className="sticky top-0 z-50 border-b border-ink/10 bg-cloud">
        <div className="mx-auto flex h-[88px] max-w-[1440px] items-center justify-between px-4 sm:px-10 lg:grid lg:h-32 lg:grid-cols-[1fr_auto_1fr]">
          <nav aria-label="Primary" className="hidden lg:block">
            <ul className="flex items-center gap-5 xl:gap-7">
              {NAV.map(([to, label, short]) => (
                <li key={to}>
                  <NavLink to={to} className={({ isActive }) => `font-a whitespace-nowrap text-[15px] transition-opacity hover:opacity-60 ${isActive ? "underline decoration-1 underline-offset-[6px]" : ""}`} data-testid={`nav-${slugOf(label)}-link`}>
                    {short ? <><span className="min-[1440px]:hidden" aria-hidden="true">{short}</span><span className="sr-only min-[1440px]:not-sr-only">{label}</span></> : label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>
          <Link to="/" aria-label="the suspects home" data-testid="header-logo" className="lg:justify-self-center">
            <Wordmark size="xl" />
          </Link>
          <div className="flex items-center gap-0 sm:gap-1 lg:justify-self-end">
            <IconBtn label="Search" onClick={() => setSearch(true)} testid="header-search-button"><Search size={20} strokeWidth={1.5} /></IconBtn>
            <AccountMenu />
            <IconBtn label={`Open cart, ${count} items`} onClick={() => setOpen(true)} testid="cart-trigger-button">
              <ShoppingBag size={20} strokeWidth={1.5} />
              <span className="absolute right-0 top-0 flex h-4 min-w-4 items-center justify-center rounded-full bg-lime px-1 font-mono text-[9px] font-bold" data-testid="cart-count-badge">{count}</span>
            </IconBtn>
            <IconBtn label="Open menu" onClick={() => setMenu(true)} testid="mobile-menu-button" className="lg:hidden"><Menu size={22} strokeWidth={1.5} /></IconBtn>
          </div>
        </div>
      </header>
      <MobileMenu open={menu} onClose={() => setMenu(false)} />
      <SearchOverlay open={search} onClose={closeSearch} />
    </>
  );
};
